import { AskNobiButton } from "@/components/AskNobi/AskNobiButton";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { PageGrid } from "@/components/PageGrid/PageGrid";
import { Button } from "@/design-system/Button";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { List } from "@/modules/community-groups/components/List/List";
import { canCreateHelpCategory } from "@/modules/community-groups/permissions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export function HelpCategoryListPage(): React.ReactNode {
  const slug = useSlug();
  const projectId = useProjectId();
  const { t } = useTranslation();
  const hasPermission = usePermission();
  const sessionUser = useSessionUser();

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={t("page.help-category.list.title")}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: sessionUser.project.name,
            },
            {
              name: t("page.help-category.list.title"),
            },
          ]}
        />
      }
      actions={
        <>
          <AskNobiButton
            defaultFilters={{ projectIds: [projectId] }}
            context={{ scope: "project", entity: "help-categories", page: "list" }}
            suggestions={[
              t("component.ask-nobi-chat-drawer.help-categories.suggestion.1"),
              t("component.ask-nobi-chat-drawer.help-categories.suggestion.2"),
            ]}
          />
          {hasPermission(canCreateHelpCategory) && (
            <Button type="link" href={routes.helpCategories.create({ slug })}>
              {t("model.help-category.action.create")}
            </Button>
          )}
        </>
      }
    >
      <PageGrid.Row>
        <PageGrid.Column size="100%">
          <List
            type="helpCategory"
            followLabels={{
              button: t("model.help-category.action.follow"),
              failedNotification: t("model.help-category.action.follow.notification-error"),
            }}
            unfollowLabels={{
              button: t("model.help-category.action.unfollow"),
              failedNotification: t("model.help-category.action.unfollow.notification-error"),
            }}
            allAudienceLabel={t("model.help-category.audience.placeholder")}
            deleteLabels={{
              confirmation: t("model.help-category.action.delete.confirmation"),
              description: t("model.help-category.action.delete.description"),
              success: t("model.help-category.action.delete.notification.success"),
              error: t("model.help-category.action.delete.notification.error"),
            }}
            statusLabels={{
              disable: {
                confirmation: t("model.platform.help-category.action.disable.confirmation"),
                description: t("model.platform.help-category.action.disable.description"),
                success: t("model.platform.help-category.action.disable.notification.success"),
                error: t("model.platform.help-category.action.disable.notification.error"),
              },
              enable: {
                success: t("model.platform.help-category.action.enable.notification.success"),
                error: t("model.platform.help-category.action.enable.notification.error"),
              },
            }}
          />
        </PageGrid.Column>
      </PageGrid.Row>
    </DocumentPage>
  );
}
